import { Updater, useImmer } from "@/app/lib/system/immer-wrapper";
import { useState, useRef } from "react";
// my imports:
import { AnyGameObj, GameMode } from "@/app/lib/game-state/game-types";
import { PyramidGameState } from "@/app/lib/game-state/pyramid-game-state";
import { StoryGameState, StorySnapshot } from "@/app/lib/game-state/story-game-state";
import { StoryLevelData } from "@/app/lib/levels/story-level-data-types";
import { FullStory, LevelID, LevelName } from "@/app/lib/levels/full-story";

export function useGameManager({
    gameVersions, puzzleDictionaryName, solutionsDictionaryName, fullStory
} : {
    gameVersions: Record<GameMode, string>,
    puzzleDictionaryName: string,
    solutionsDictionaryName: string,
    fullStory: FullStory
}) {
    // state that manages which game we're on
    const [activeMode, setActiveMode] = useState<GameMode>('pyramid');
    // our pyramid object
    const pyramid = usePyramid(gameVersions['pyramid']);
    // our story object
    const story = useStory(gameVersions['story']);
    // manages level state for story:
    const levels = useStoryLevel(fullStory, story.update);

    // "AnyGame" is the GameStateObject return type that corresponds to shape of parent class
    function getActive() : AnyGameObj {

        switch(activeMode) {
            case 'pyramid': 
                // we upcast to the parent class
                return {
                    state: pyramid.state,
                    update: (recipe) => {
                        /* This is a magic cast. TypeScript is annoying AF about polymorphism(?)
                        In function parameters. So we can't just return pyramid directly. It thinks the
                        Updater functions don't match up. */
                        pyramid.update(recipe as any);
                    }
                };
            case 'story':
                // again upcast
                return {
                    state: story.state,
                    update: (recipe) => {
                        story.update(recipe as any);
                    }
                };
        }
    }

    function toggleActiveGame() {
        if (activeMode === 'pyramid') {
            setActiveMode('story');
        }
        else if (activeMode === 'story') {
            setActiveMode('pyramid');
        }
    }

    const dictionaries = {
        puzzle: puzzleDictionaryName,
        solutions: solutionsDictionaryName
    }

    return {
        activeMode: activeMode,
        setActiveMode: setActiveMode,
        toggleGame: toggleActiveGame,
        versions: gameVersions,
        active: getActive(),
        dictionaries,
        pyramid,
        story,
        levels
    };
}

export type PyramidGameObj = ReturnType<typeof usePyramid>;

function usePyramid(version: string) {
    const [state, update] = useImmer<PyramidGameState>(
        () => new PyramidGameState(version)
    );

    return { state, update };
}

export type StoryGameObj = ReturnType<typeof useStory>;

function useStory(version: string) {
    const [state, update] = useImmer<StoryGameState>(
        () => new StoryGameState(version)
    );
    return  { state, update }
}

function useStoryLevel(fullStory: FullStory, updateStory: Updater<StoryGameState>) {
    const finalLevel = fullStory.finalLevel;

    const [max, setMax] = useState(finalLevel);
    const [current, setCurrent] = useState(1);

    const snapshotMapRef = useRef<Map<LevelID, StorySnapshot>>(new Map());
    const snapshotMap = snapshotMapRef.current;

    function changeLevel(level: number) {
        // the level has to exist
        const levelData: StoryLevelData | null = fullStory.getLevelByNumber(level);
        if (!levelData)
            return;

        const snapshot : StorySnapshot | undefined = snapshotMap.get(levelData.levelID);
        // if we have a snapshot in memory, we use that
        if (snapshot) {
            updateStory(state => {
                state.importSnapshot(snapshot);
            });
        } else {
            updateStory(state => {
                state.buildPuzzle(levelData);
            });
        }
        setCurrent(level);
    }

    function resetCurrent() {
        // rebuild the level
        const levelData: StoryLevelData | null = fullStory.getLevelByNumber(current);
        if (!levelData)
            return;

        updateStory(state => {
            state.buildPuzzle(levelData);
        })
    }

    function getCurrentData() : StoryLevelData | null {
        const levelData = fullStory.getLevelByNumber(current);
        if (levelData) {
            return levelData;
        }
        return null;
    }

    // currently unused, we want to use changeLevel and max shouldn't change:
    const set = {
        max: (level: number) => {
            if (level > finalLevel)
                return;
            setMax(level);
        },
        current: (level: number) => {
            if (level < 1 || level > max)
                return;
            changeLevel(level);
        }
    }    
    const move = {
        toMax: () => {
            changeLevel(max);
        },
        toStart: () => {
            changeLevel(1);
        },
        forward: () => {
            if (current < max)
                changeLevel(current + 1);
        },
        back: () => {
            if (current > 1)
                changeLevel(current - 1);
        },
        byID: (levelID: LevelID) => {
            const levelData = fullStory.getLevelByID(levelID);
            if (levelData === null) {
                return;
            }
            changeLevel(levelData.levelNumber);
        },
        byName: (levelName: LevelName) => {
            const levelData = fullStory.getLevelByName(levelName);
            if (levelData === null) {
                return;
            }
            changeLevel(levelData.levelNumber);
        }
    }

    return {max, current, move, snapshotMap, fullStory, changeLevel, resetCurrent, getCurrentData}
}

// return type alias
export type GameManager = ReturnType<typeof useGameManager>;